import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    styled,
    Stack,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useState, useEffect } from 'react';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import { z } from 'zod';
import { IBeginWorkOnSuccessArgs } from 'src/types';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Title from 'src/components/Title';

import { UserIcon } from 'src/icons/UserIcon';
import { TgArrowIcon } from 'src/icons/TgArrowIcon';
import { CaseIcon } from 'src/icons/CaseIcon';
import UserSevice from 'src/services/UserSevice';
import useUserStore from 'src/stores/user';
import { AIIcon } from 'src/icons/AIIcon';
import { IUserDescription } from 'src/types/User';
import CustomInputField from 'src/components/CustomInputField';

const ProfileSchema = z
    .object({
        first_name: z.string().optional(),
        tg_account: z.string().optional(),
        business_description: z.string().min(1, 'Опишите вашу деятельность').max(150, 'Не более 150 символов'),
        user_role: z.string().min(1, 'Выберите вашу деятельность'),
        custom_role: z.string().max(40, 'Не более 40 символов').optional(),
        website_url: z.string().optional(),
        crm_system: z.string().optional(),
    })
    .refine(
        (data) => {
            if (data.user_role === 'other') {
                return data.custom_role && data.custom_role.trim().length > 0;
            }
            return true;
        },
        {
            message: 'Введите вашу деятельность',
            path: ['custom_role'],
        }
    );

const CustomTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': { borderRadius: '16px' },
    backgroundColor: '#fff',
    // --- Новый стиль для value вне фокуса ---
    '& .MuiInputBase-root:not(.Mui-focused) .MuiInputBase-input:not(:placeholder-shown)': {
        color: theme.palette.primary.main,
        opacity: 1,
    },
    // --- Стили для обводки активного инпута ---
    '& .MuiOutlinedInput-root': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
    },
}));

export const MaxChars = ({
    isError,
    charCount,
    maxChars,
}: {
    isError: boolean;
    charCount: number;
    maxChars: number;
}) => {
    return (
        <Stack
            gap="2px"
            direction="row"
            alignItems="center"
            sx={{ mt: 0.5 }}
        >
            {!isError && <AIIcon />}

            <Typography
                variant="body2"
                sx={{
                    background: isError ? 'none' : 'linear-gradient(90deg, #FF88C0, #9FA9BC)',
                    WebkitBackgroundClip: isError ? 'none' : 'text',
                    color: isError ? 'error.main' : 'transparent',
                }}
            >
                {isError ? `Превышено количество символов (максимум ${maxChars})` : `${charCount}/${maxChars}`}
            </Typography>
        </Stack>
    );
};

export interface IChatsAndChannelsScreenProps {
    onSuccess?: ({ skiped }: IBeginWorkOnSuccessArgs) => void;
}

export default function DescribeYourBusiness({ onSuccess }: IChatsAndChannelsScreenProps) {
    const { enqueueSnackbar } = useSnackbar();
    const userInfo = useUserStore((state) => ({
        first_name: state.first_name,
        tg_account: state.tg_account,
        business_description: state.business_description,
        user_role: state.user_role,
        isOnboardingCompleted: state.isOnboardingCompleted,
        tg_notification_mode: state.tg_notification_mode,
        onboarding_step: String(state.onboardingStep),
        is_onboarding_completed: state.isOnboardingCompleted,
        is_email_confirmed: state.isEmailConfirmed,
        bonus_points: state.bonusPoints,
        current_subscription: state.current_subscription,
        email: state.email,
        id: state.id,
        username: state.username,
        role: state.role,
        website_url: state.website_url,
        crm_system: state.crm_system || '',
        tg_notifications_enabled: state.tg_notifications_enabled,
        is_telegram_connected: state.is_telegram_connected,
    }));

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        watch,
    } = useForm({
        defaultValues: {
            first_name: userInfo.first_name || '',
            tg_account: userInfo.tg_account || '',
            business_description: userInfo.business_description || '',
            user_role: userInfo.user_role || '',
            custom_role: '',
            website_url: userInfo.website_url || '',
            crm_system: userInfo.crm_system || '',
        },
        resolver: zodResolver(ProfileSchema),
        mode: 'onChange',
        resetOptions: {
            keepIsSubmitted: true,
        },
    });

    const formValues = watch();

    const queryClient = useQueryClient();

    const { mutate: updateDescription, isLoading: isUpdateDescriptionLoading } = useMutation(
        async (data: IUserDescription) => {
            await UserSevice.updateDescription(data);
            queryClient.invalidateQueries('user');
        },
        {
            onSuccess: async () => {
                // Обновляем данные пользователя в store
                try {
                    const updatedUser = await UserSevice.getUser();
                    useUserStore.getState().setUserInfo(updatedUser);

                    // Проверяем, что шаг онбординга уже обновлен на бэкенде
                    // Бэкенд уже обновил шаг на 'choose_stream' при сохранении description
                    if (updatedUser.onboarding_step === 'choose_stream') {
                        // Шаг уже обновлен на бэкенде, просто вызываем onSuccess для перехода
                        // Не показываем уведомление и не вызываем updateBeginWorkStep
                        if (onSuccess) onSuccess({ skiped: false });
                        return;
                    }
                } catch (error) {
                    console.error('Ошибка обновления store:', error);
                }

                // Если шаг не обновлен на бэкенде (не должно происходить, но на всякий случай)
                // Показываем уведомление и вызываем onSuccess
                enqueueSnackbar('Все данные успешно обновлены', { variant: 'success' });
                if (onSuccess) onSuccess({ skiped: false });
            },
            onError: () => {
                enqueueSnackbar('Ошибка при обновлении данных', { variant: 'error' });
            },
        }
    );

    const onSubmit = async (
        data: IUserDescription & { user_role: string; custom_role?: string; crm_system?: string }
    ) => {
        const roleValue = data.user_role === 'other' ? data.custom_role : data.user_role;

        updateDescription({
            tg_account: data.tg_account,
            business_description: data.business_description,
            first_name: data.first_name,
            user_role: roleValue,
            website_url: data.website_url,
            crm_system: data.crm_system,
        });
    };

    const isFormChanged = Object.keys(formValues).some((key) => {
        if (key === 'custom_role') {
            return formValues[key] !== '';
        }
        return formValues[key] !== (userInfo[key] ?? '');
    });
    const isLoading = isUpdateDescriptionLoading;
    const isDisabled = isLoading || !isFormChanged || !isValid;

    return (
        <LoadingWrapper isLoading={false}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box mb={1.5}>
                    <Title>{userInfo.isOnboardingCompleted ? 'Профиль' : 'Расскажите о своём бизнесе'}</Title>
                </Box>
                <Box
                    maxWidth="80%"
                    mb={20 / 8}
                >
                    <Typography
                        variant="body1"
                        color="primary"
                    >
                        {userInfo.isOnboardingCompleted
                            ? 'Информация про ваш бизнес и целевых клиентов важна для точного определения целевых сообщений с помощью ChatGPT'
                            : 'Чтобы мы адаптировали сервис под ваши задачи, расскажите немного о себе и бизнесе'}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="first_name"
                                sx={{ fontWeight: 600 }}
                            >
                                Ваше имя
                            </Typography>
                            <Controller
                                name="first_name"
                                control={control}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        id="first_name"
                                        fullWidth
                                        placeholder="Введите ваше имя"
                                        error={!!errors.first_name}
                                        helperText={errors.first_name?.message}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <UserIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="tg_account"
                                sx={{ fontWeight: 600 }}
                            >
                                Ваш Telegram аккаунт
                            </Typography>
                            <Controller
                                name="tg_account"
                                control={control}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        id="tg_account"
                                        placeholder="@username"
                                        fullWidth
                                        error={!!errors.tg_account}
                                        helperText={errors.tg_account?.message}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TgArrowIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="user_role"
                                sx={{ fontWeight: 600 }}
                            >
                                Ваша деятельность
                            </Typography>
                            <Controller
                                name="user_role"
                                control={control}
                                render={({ field }) => (
                                    <FormControl
                                        fullWidth
                                        error={!!errors.user_role}
                                    >
                                        <Select
                                            {...field}
                                            id="user_role"
                                            displayEmpty
                                            sx={{
                                                borderRadius: '16px',
                                                backgroundColor: '#fff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'transparent',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#000000',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#000000',
                                                },
                                                '& .MuiSelect-select': {
                                                    color: field.value ? '#000000' : 'rgba(0, 0, 0, 0.6)',
                                                },
                                                '&.Mui-focused .MuiSelect-select': {
                                                    color: '#000000',
                                                },
                                            }}
                                        >
                                            <MenuItem
                                                value=""
                                                disabled
                                            >
                                                Выберите вашу деятельность
                                            </MenuItem>
                                            <MenuItem value="business_owner">Бизнес</MenuItem>
                                            <MenuItem value="marketer">Маркетинг</MenuItem>
                                            <MenuItem value="freelancer">Фриланс</MenuItem>
                                            <MenuItem value="professional">Профессионал</MenuItem>
                                            <MenuItem value="advertising_agency">Рекламное агентство</MenuItem>
                                            <MenuItem value="hr">HR</MenuItem>
                                            <MenuItem value="other">Другое</MenuItem>
                                        </Select>
                                        {errors.user_role && (
                                            <FormHelperText>{errors.user_role.message as string}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Stack>

                        {watch('user_role') === 'other' && (
                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="custom_role"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Ваш вариант
                                </Typography>
                                <Controller
                                    name="custom_role"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomTextField
                                            {...field}
                                            id="custom_role"
                                            fullWidth
                                            placeholder="Введите вашу деятельность"
                                            error={!!errors.custom_role}
                                            helperText={errors.custom_role?.message}
                                            inputProps={{ maxLength: 40 }}
                                        />
                                    )}
                                />
                            </Stack>
                        )}

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="business_description"
                                sx={{ fontWeight: 600 }}
                            >
                                Опишите вашу деятельность
                            </Typography>
                            <Controller
                                name="business_description"
                                control={control}
                                render={({ field }) => {
                                    const charCount = field.value.length;
                                    const isError = charCount > 150;

                                    return (
                                        <>
                                            <CustomTextField
                                                {...field}
                                                id="business_description"
                                                placeholder="Опишите максимально подробно для лучшего результата работы ТГХ"
                                                fullWidth
                                                multiline
                                                rows={4}
                                                error={isError}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CaseIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: '134px',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    },
                                                }}
                                            />

                                            <MaxChars
                                                isError={isError}
                                                charCount={charCount}
                                                maxChars={150}
                                            />
                                        </>
                                    );
                                }}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="website_url"
                                sx={{ fontWeight: 600 }}
                            >
                                Ссылка на сайт (если есть)
                            </Typography>
                            <Controller
                                name="website_url"
                                control={control}
                                render={({ field }) => (
                                    <CustomInputField
                                        {...field}
                                        placeholder="https://yoursite.ru"
                                        name="website_url"
                                        icon="link"
                                    />
                                )}
                            />
                        </Stack>

                        <Stack
                            gap={1}
                            mb={3}
                        >
                            <Typography
                                component="label"
                                htmlFor="crm_system"
                                sx={{ fontWeight: 600 }}
                            >
                                Укажите вашу CRM систему (если пользуетесь)
                            </Typography>
                            <Controller
                                name="crm_system"
                                control={control}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        id="crm_system"
                                        fullWidth
                                        placeholder="Например: Битрикс24, AmoCRM, Salesforce"
                                        error={!!errors.crm_system}
                                        helperText={errors.crm_system?.message}
                                    />
                                )}
                            />
                        </Stack>

                        <Box
                            display="flex"
                            alignItems="flex-end"
                            flexGrow={1}
                            pb={1}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                type="submit"
                                disabled={isDisabled}
                            >
                                {isLoading ? <CircularProgress size={20} /> : 'Сохранить'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Box>
        </LoadingWrapper>
    );
}
